import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, CheckCircle, XCircle, Eye, FileImage, Film, Clock } from 'lucide-react';
import HumanVerifiedBadge from './HumanVerifiedBadge';

const AdminVerificationsTab = ({ backendUrl }) => {
  const [verifications, setVerifications] = useState([]);
  const [verificationStats, setVerificationStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    loadVerifications();
    loadStats();
  }, [page]);

  const loadVerifications = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/verifications/pending`, {
        params: { page, limit: 20 },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setVerifications(response.data.verifications || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load verifications:', error);
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/verifications/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setVerificationStats(response.data);
    } catch (error) {
      console.error('Failed to load verification stats:', error);
    }
  };

  const handleReview = async (profileId, action, notes = '') => {
    if (!notes && action === 'reject') {
      notes = prompt('Please provide a reason for rejection:');
      if (!notes) return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        `${backendUrl}/api/admin/verifications/${profileId}/review`,
        { action, notes },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      loadVerifications();
      loadStats();
      setSelectedVerification(null);
      alert(`Verification ${action}ed successfully!`);
    } catch (error) {
      console.error('Failed to review verification:', error);
      alert(error.response?.data?.detail || 'Failed to review verification');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12 text-zinc-400">
        Loading verifications...
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stats */}
      {verificationStats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard
            label="Total Verified"
            value={verificationStats.total_verified}
            color="blue"
            icon={<Shield className="w-5 h-5" />}
          />
          <StatCard
            label="Pending"
            value={verificationStats.pending}
            color="yellow"
            icon={<Clock className="w-5 h-5" />}
          />
          <StatCard
            label="Approved"
            value={verificationStats.approved}
            color="green"
            icon={<CheckCircle className="w-5 h-5" />}
          />
          <StatCard
            label="Rejected"
            value={verificationStats.rejected}
            color="red"
            icon={<XCircle className="w-5 h-5" />}
          />
          <StatCard
            label="Verification Rate"
            value={`${verificationStats.verification_rate}%`}
            color="purple"
            icon={<Shield className="w-5 h-5" />}
          />
        </div>
      )}

      {/* Verifications List */}
      <div>
        <h2 className="text-2xl font-bold mb-6">Pending Verifications</h2>
        
        {verifications.length === 0 ? (
          <div className="bg-zinc-900 rounded-xl p-12 text-center">
            <Shield className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
            <p className="text-zinc-400 text-lg">No pending verifications</p>
          </div>
        ) : (
          <div className="space-y-4">
            {verifications.map((verification) => (
              <VerificationCard
                key={verification.id}
                verification={verification}
                onReview={handleReview}
                onViewDetails={() => setSelectedVerification(verification)}
                loading={actionLoading}
              />
            ))}
          </div>
        )}
      </div>

      {/* Verification Details Modal */}
      {selectedVerification && (
        <VerificationModal
          verification={selectedVerification}
          onClose={() => setSelectedVerification(null)}
          onReview={handleReview}
          loading={actionLoading}
          backendUrl={backendUrl}
        />
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, color, icon }) => {
  const colors = {
    blue: 'from-blue-600 to-blue-500',
    yellow: 'from-yellow-600 to-yellow-500',
    green: 'from-green-600 to-green-500',
    red: 'from-red-600 to-red-500',
    purple: 'from-purple-600 to-purple-500'
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl p-4`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-white/80 text-sm">{label}</span>
        <div className="text-white/80">{icon}</div>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
    </div>
  );
};

// Verification Card Component
const VerificationCard = ({ verification, onReview, onViewDetails, loading }) => {
  return (
    <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-semibold text-lg">{verification.display_name || 'Creator'}</h3>
            <span className="text-sm text-zinc-400">@{verification.handle}</span>
          </div>
          <p className="text-sm text-zinc-400">{verification.user_email}</p>
          <p className="text-sm text-zinc-500 mt-1">
            Submitted: {new Date(verification.verification_submitted_at).toLocaleString()}
          </p>
        </div>
        <button
          onClick={onViewDetails}
          className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm flex items-center gap-2"
        >
          <Eye className="w-4 h-4" />
          View Details
        </button>
      </div>

      {verification.verification_notes && (
        <div className="bg-zinc-800 rounded-lg p-4 mb-4">
          <p className="text-sm text-zinc-300">{verification.verification_notes}</p>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <span className="text-sm text-zinc-400">
          Proof Files: {verification.verification_proof?.length || 0}
        </span>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => onReview(verification.id, 'approve')}
          disabled={loading}
          className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <CheckCircle className="w-4 h-4" />
          Approve
        </button>
        <button
          onClick={() => onReview(verification.id, 'reject')}
          disabled={loading}
          className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <XCircle className="w-4 h-4" />
          Reject
        </button>
      </div>
    </div>
  );
};

// Verification Details Modal
const VerificationModal = ({ verification, onClose, onReview, loading, backendUrl }) => {
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-zinc-800">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-2">{verification.display_name || 'Creator'}</h2>
              <p className="text-zinc-400">@{verification.handle}</p>
              <p className="text-sm text-zinc-500">{verification.user_email}</p>
            </div>
            <button
              onClick={onClose}
              className="text-zinc-400 hover:text-white"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Submission Info */}
          <div>
            <h3 className="font-semibold mb-2">Submission Details</h3>
            <div className="bg-zinc-800 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-zinc-400">Submitted:</span>
                <span>{new Date(verification.verification_submitted_at).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-400">Files Uploaded:</span>
                <span>{verification.verification_proof?.length || 0}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {verification.verification_notes && (
            <div>
              <h3 className="font-semibold mb-2">Creator Notes</h3>
              <div className="bg-zinc-800 rounded-lg p-4">
                <p className="text-zinc-300">{verification.verification_notes}</p>
              </div>
            </div>
          )}

          {/* Proof Files */}
          {verification.verification_proof && verification.verification_proof.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">Proof Files</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {verification.verification_proof.map((file, index) => (
                  <div key={index} className="bg-zinc-800 rounded-lg overflow-hidden">
                    {file.url && (
                      <a href={`${backendUrl}${file.url}`} target="_blank" rel="noopener noreferrer">
                        {file.url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                          <img
                            src={`${backendUrl}${file.url}`}
                            alt={file.original_name}
                            className="w-full h-40 object-cover"
                          />
                        ) : (
                          <div className="w-full h-40 flex items-center justify-center">
                            <Film className="w-12 h-12 text-zinc-500" />
                          </div>
                        )}
                      </a>
                    )}
                    <div className="p-2">
                      <p className="text-xs text-zinc-400 truncate">{file.original_name}</p>
                      <p className="text-xs text-zinc-600 capitalize">{file.type}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t border-zinc-800">
            <button
              onClick={() => {
                if (window.confirm('Approve this verification request?')) {
                  onReview(verification.id, 'approve');
                }
              }}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <CheckCircle className="w-5 h-5" />
              Approve Verification
            </button>
            <button
              onClick={() => {
                const notes = prompt('Please provide a reason for rejection:');
                if (notes) {
                  onReview(verification.id, 'reject', notes);
                }
              }}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <XCircle className="w-5 h-5" />
              Reject
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminVerificationsTab;
